import React, { FC, useEffect, useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";

import { ChartsProps } from "./chart.types";
import { chartMapper } from "../../utils/chartMapper";
import { chartOptionMap } from "./chartOptions";

const Charts: FC<ChartsProps> = ({
  chartType,
  displayType,
  chartData,
  responseType,
  width = "100%",
  height = "850px",
}) => {
  const isTreatmentDetails =
    String(responseType ?? displayType).toLowerCase().trim() ===
    "treatment_details";
  const isTreatmentTypes =
    String(responseType ?? "").toLowerCase().trim() === "treatment_types";
  const effectiveChartType = chartType;
  const effectiveDisplayType = isTreatmentDetails
    ? "treatment_details"
    : isTreatmentTypes
      ? "treatment_types"
      : displayType;

  const [viewport, setViewport] = useState<"mobile" | "tablet" | "laptop">(
    () => {
      if (typeof window === "undefined") return "laptop";
      if (window.innerWidth < 576) return "mobile";
      if (window.innerWidth < 992) return "tablet";
      return "laptop";
    },
  );

  useEffect(() => {
    const updateViewport = () => {
      if (window.innerWidth < 576) {
        setViewport("mobile");
      } else if (window.innerWidth < 992) {
        setViewport("tablet");
      } else {
        setViewport("laptop");
      }
    };

    updateViewport();
    window.addEventListener("resize", updateViewport);

    return () => window.removeEventListener("resize", updateViewport);
  }, []);
  /**
   * Transform API response into the
   * format required by the selected chart.
   */
  const mappedData = useMemo(() => {
    return chartMapper(
      effectiveChartType,
      effectiveDisplayType,
      chartData,
      displayType,
    );
  }, [effectiveChartType, effectiveDisplayType, chartData, displayType]);

  //Calculate the Height
  // const chartHeight = useMemo(() => {
  //   // Only make Bar Chart dynamic
  //   if (chartType !== "bar") {
  //     return height;
  //   }

  //   const categoryCount = Array.isArray(mappedData?.categories)
  //     ? mappedData.categories.length
  //     : 0;

  //   const dynamicHeight = Math.max(500, categoryCount * 35);

  //   return `${dynamicHeight}px`;
  // }, [chartType, mappedData, height]);
  const chartHeight = useMemo(() => {
    const baseHeight =
      typeof height === "number" ? height : parseInt(String(height), 10) || 850;

    const responsiveHeight =
      viewport === "mobile"
        ? Math.min(baseHeight, 420)
        : viewport === "tablet"
          ? Math.min(baseHeight, 560)
          : baseHeight;

    if (effectiveChartType !== "bar") {
      return `${Math.max(320, responsiveHeight)}px`;
    }

    const categoryCount = Array.isArray(mappedData?.categories)
      ? mappedData.categories.length
      : 0;

    const dynamicHeight = categoryCount * 32 + 100;
    const minHeight =
      viewport === "mobile" ? 380 : viewport === "tablet" ? 500 : 650;

    // For mobile/tablet, allow the chart to shrink if there are few categories
    if (viewport === "mobile" || viewport === "tablet") {
      return `${Math.max(minHeight, dynamicHeight)}px`;
    }

    return `${Math.max(minHeight, Math.max(baseHeight, dynamicHeight))}px`;
  }, [effectiveChartType, mappedData, height, viewport]);

  const option = useMemo(() => {
    const optionGenerator = chartOptionMap[effectiveChartType];

    if (!optionGenerator) {
      console.error(`Chart '${effectiveChartType}' is not supported.`);
      return {};
    }

    const generatedOption = optionGenerator(mappedData);
    let finalOption: any = { ...generatedOption };

    // Keep population bars horizontal; cancer treatment bars use ICCC3 on x.
    if (effectiveChartType === "bar" && mappedData?.orientation !== "vertical") {
      const originalXAxis = generatedOption.xAxis as any;
      const originalYAxis = generatedOption.yAxis as any;
      const isStacked = Array.isArray(mappedData?.series)
        && mappedData.series.some((series: any) => series?.stack);

      finalOption = {
        ...generatedOption,
        xAxis: {
          ...originalYAxis,
          type: "value",
          name: "Cases",
        },
        yAxis: {
          ...originalXAxis,
          type: "category",
          data: mappedData.categories,
          name: mappedData.axisName,
          axisLabel: {
            interval: 0,
            rotate: 0,
            formatter: (value: string) => {
              if (typeof value === "string" && value.length > 25) {
                return value.slice(0, 22) + "...";
              }
              return value;
            },
          },
        },
        series: Array.isArray(generatedOption.series)
          ? generatedOption.series.map((s: any) => ({
              ...s,
              label: {
                ...s.label,
                  position: isStacked ? "inside" : "right",
                color: "#ffffff",
                  ...(isStacked ? { align: "center", distance: 0 } : {}),
              },
            }))
          : generatedOption.series,
      };
    } else if (
      effectiveChartType === "bar" &&
      mappedData?.orientation === "vertical"
    ) {
      finalOption = {
        ...generatedOption,
        xAxis: {
          ...(generatedOption.yAxis as any),
          type: "category",
          data: mappedData.categories,
          name: mappedData.axisName,
          axisLabel: {
            interval: 0,
            rotate: 0,
          },
        },
        yAxis: {
          ...(generatedOption.xAxis as any),
          type: "value",
          name: "Cases",
        },
        series: Array.isArray(generatedOption.series)
          ? generatedOption.series.map((s: any) => ({
              ...s,
              label: {
                ...s.label,
                position: "top",
                color: "#ffffff",
              },
            }))
          : generatedOption.series,
      };
    }

    // Force white color styling for contrast on dark background
    if (finalOption.title) {
      finalOption.title = {
        ...finalOption.title,
        textStyle: { color: "#ffffff", ...(finalOption.title.textStyle || {}) },
      };
    }

    if (finalOption.legend) {
      finalOption.legend = {
        ...finalOption.legend,
        textStyle: { color: "#ffffff", ...(finalOption.legend.textStyle || {}) },
      };
    }

    if (finalOption.xAxis) {
      finalOption.xAxis = Array.isArray(finalOption.xAxis)
        ? finalOption.xAxis.map((x: any) => ({
            ...x,
            axisLabel: { color: "#ffffff", ...(x.axisLabel || {}) },
            nameTextStyle: { color: "#ffffff", ...(x.nameTextStyle || {}) },
          }))
        : {
            ...finalOption.xAxis,
            axisLabel: { color: "#ffffff", ...(finalOption.xAxis.axisLabel || {}) },
            nameTextStyle: { color: "#ffffff", ...(finalOption.xAxis.nameTextStyle || {}) },
          };
    }

    if (finalOption.yAxis) {
      finalOption.yAxis = Array.isArray(finalOption.yAxis)
        ? finalOption.yAxis.map((y: any) => ({
            ...y,
            axisLabel: { color: "#ffffff", ...(y.axisLabel || {}) },
            nameTextStyle: { color: "#ffffff", ...(y.nameTextStyle || {}) },
          }))
        : {
            ...finalOption.yAxis,
            axisLabel: { color: "#ffffff", ...(finalOption.yAxis.axisLabel || {}) },
            nameTextStyle: { color: "#ffffff", ...(finalOption.yAxis.nameTextStyle || {}) },
          };
    }

    return finalOption;
  }, [effectiveChartType, mappedData]);

  return (
    <div
      className={`responsive-chart-shell responsive-chart-${effectiveChartType}`}
      data-chart-type={effectiveChartType}
      style={{ width: "100%", maxWidth: "100%", height: chartHeight }}
    >
      <ReactECharts
        option={option}
        style={{
          width: "100%",
          height: "100%",
        }}
        notMerge
        lazyUpdate
      />
    </div>
  );
};

export default Charts;
